import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api';
import {
  UserCheck,
  Gavel,
  Building2,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  Check,
  Search,
  ShieldCheck,
  Activity,
  Sparkles,
  EyeOff,
  Settings2,
  Wrench,
} from 'lucide-react';

type View =
  | 'users'
  | 'roles'
  | 'regulations'
  | 'satkers'
  | 'opa'
  | 'audit-settings'
  | 'feature-flags'
  | 'system';

const TITLES: Record<View, { label: string; latin: string; icon: any }> = {
  'users':          { label: 'Pengguna',          latin: 'USERS',         icon: UserCheck },
  'roles':          { label: 'Roles & Permission', latin: 'RBAC',         icon: ShieldCheck },
  'regulations':    { label: 'KB Regulasi (UU)',  latin: 'NORMA',         icon: Gavel },
  'satkers':        { label: 'Satker',            latin: 'ORGANIZATIO',   icon: Building2 },
  'opa':            { label: 'Kebijakan OPA',     latin: 'POLITIA',       icon: ShieldCheck },
  'audit-settings': { label: 'Audit Settings',    latin: 'AUDITORIA',     icon: Activity },
  'feature-flags':  { label: 'Feature Flags',     latin: 'TOGGLES',       icon: Sparkles },
  'system':         { label: 'Sistem',            latin: 'INFRA',         icon: Settings2 },
};

export default function AdminPage() {
  const [searchParams] = useSearchParams();
  const view = (searchParams.get('view') as View) || 'users';
  const title = TITLES[view] || TITLES['users'];
  const Icon = title.icon;

  return (
    <div>
      <div className="mb-4 pb-2 border-b-2 border-ink flex items-baseline gap-3 flex-wrap">
        <Icon className="w-5 h-5 text-brass self-center" />
        <h1 className="font-display font-bold text-[22px] leading-tight tracking-tight">{title.label}</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">— Administrasi · {title.latin}</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-3">Master Data</span>
      </div>

      {view === 'users' && <UsersView />}
      {view === 'roles' && <RolesView />}
      {view === 'regulations' && <RegulationsView />}
      {view === 'satkers' && <SatkersView />}
      {view === 'opa' && <ComingSoon label="Kebijakan OPA (ABAC)" desc="Editor policy Rego, simulator request, audit policy decisions. Membutuhkan integrasi OPA daemon yang sudah jalan di netra-opa." />}
      {view === 'audit-settings' && <ComingSoon label="Audit Settings" desc="Retensi event, jadwal export otomatis, alerting threshold untuk anomali aktivitas." />}
      {view === 'feature-flags' && <ComingSoon label="Feature Flags" desc="Toggle modul AI per environment, A/B test prompt versions, kill switch." />}
      {view === 'system' && <SystemView />}
    </div>
  );
}

function ComingSoon({ label, desc }: { label: string; desc: string }) {
  return (
    <section>
      <div className="bg-paper-2/30 border-2 border-dashed border-ink-line p-12 text-center">
        <Wrench className="w-12 h-12 mx-auto mb-4 text-ink-3 opacity-50" />
        <h2 className="font-display font-bold text-[20px] tracking-tight mb-2">{label}</h2>
        <p className="font-mono text-[12px] text-ink-2 leading-relaxed max-w-xl mx-auto">{desc}</p>
        <div className="mt-4 inline-block px-3 py-1 font-mono text-[10px] uppercase tracking-widest border border-bluestamp text-bluestamp">
          Coming Soon
        </div>
      </div>
    </section>
  );
}

function SystemView() {
  return (
    <section className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SysCard label="Backend" status="OK" detail="netra-backend · FastAPI · :8011" />
        <SysCard label="Worker" status="OK" detail="netra-worker · Celery · ingest/OCR pipeline" />
        <SysCard label="Database" status="OK" detail="Postgres · schema netra · Alembic migrated" />
        <SysCard label="Redis" status="OK" detail="netra-redis · broker Celery + cache" />
        <SysCard label="MinIO" status="OK" detail="netra-minio · bucket netra-bap (S3)" />
        <SysCard label="OpenSearch" status="OK" detail="Hybrid retrieval · BM25 + kNN HNSW 768d" />
        <SysCard label="OPA" status="OK" detail="ABAC Rego · 15/15 tests · clearance + role + satker" />
        <SysCard label="Keycloak" status="OPT-IN" detail="profile=oidc · default OFF · local auth aktif" />
        <SysCard label="LLM" status="OK" detail="Ollama gpt-oss:120b-cloud (extraction + Q&A)" />
        <SysCard label="Embedding" status="OK" detail="nomic-embed-text 768d · dipakai untuk kNN indexing" />
        <SysCard label="OCR" status="OK" detail="PyMuPDF rasterize 200dpi + Tesseract ind+eng" />
        <SysCard label="Auth" status="OK" detail="HS256 lokal (Argon2) · RS256 dual-path Keycloak" />
      </div>

      <div>
        <h3 className="font-display font-bold text-[16px] mb-3">Pipeline & Modul</h3>
        <table className="w-full font-mono text-[11px] border border-ink-line">
          <tbody>
            <RowKV label="Ingest" value="Upload → MinIO → Celery → text/OCR → chunk → embed → OpenSearch + Postgres" />
            <RowKV label="Retrieval" value="Hybrid: BM25 (OpenSearch) + kNN vektor 768d (HNSW)" />
            <RowKV label="PII" value="8 built-in rules (NIK/NPWP/HP/Email/Plat/Rekening/Person/Address) · CRUD via /privacy" />
            <RowKV label="Audit trail" value="Hash-chain SHA-256 · immutable · /audit" />
            <RowKV label="RBAC" value="DB-backed allowed_tabs · OPA per-BAP clearance + satker scope" />
            <RowKV label="Export" value="WeasyPrint (PDF) + htmldocx (DOCX)" />
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="font-display font-bold text-[16px] mb-3">Quota & Retention</h3>
        <table className="w-full font-mono text-[11px] border border-ink-line">
          <tbody>
            <RowKV label="Max upload size" value="20 MB" />
            <RowKV label="Embedding dim" value="768 (nomic-embed-text)" />
            <RowKV label="Chunk retention" value="365 hari" />
            <RowKV label="Audit retention" value="2 tahun (immutable hash-chain)" />
            <RowKV label="LLM token quota" value="Unlimited (cloud LLM)" />
            <RowKV label="Concurrent scans" value="Celery worker pool (async)" />
          </tbody>
        </table>
      </div>

      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
        Snapshot read-only · panel settings interaktif menyusul
      </p>
    </section>
  );
}

function SysCard({ label, status, detail }: { label: string; status: string; detail: string }) {
  const color =
    status === 'OK' ? 'text-olive border-olive' :
    status === 'STUB' || status === 'DEV BYPASS' || status === 'UNUSED' ? 'text-bluestamp border-bluestamp' :
    'text-crimson border-crimson';
  return (
    <div className="bg-paper-2/40 border border-ink-line p-3">
      <div className="font-display font-bold text-[14px] mb-1">{label}</div>
      <span className={`inline-block font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${color} font-bold mb-2`}>
        {status}
      </span>
      <p className="font-mono text-[10px] text-ink-2 leading-snug">{detail}</p>
    </div>
  );
}

function RowKV({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-dashed border-ink-line/60 last:border-b-0">
      <td className="px-3 py-2 text-ink-3 uppercase tracking-wider text-[10px] w-1/3">{label}</td>
      <td className="px-3 py-2 text-ink font-bold">{value}</td>
    </tr>
  );
}

// ============ USERS ============

interface UserRow {
  id: string;
  email: string;
  full_name: string;
  role: string;
  satker: string | null;
  region: string | null;
  max_clearance: string;
  nrp: string | null;
  is_active: boolean;
  mfa_enabled: boolean;
  last_login_at: string | null;
}

const ROLES = ['super_admin', 'admin', 'auditor', 'penyidik', 'analis', 'jaksa', 'viewer', 'privacy_eng'];
const CLEARANCES = ['BIASA', 'TERBATAS', 'RAHASIA', 'SANGAT_RAHASIA'];

function UsersView() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: users, isLoading } = useQuery<UserRow[]>({
    queryKey: ['admin-users', q],
    queryFn: () => api(`/api/v1/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });

  return (
    <section>
      <div className="mb-4 flex items-center gap-2 justify-end">
        <div className="flex items-center gap-2 px-3 h-9 border border-ink-line bg-paper">
          <Search className="w-3 h-3 text-ink-3" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama / email..."
            className="font-mono text-[11px] bg-transparent outline-none w-44"
          />
        </div>
        <button
          onClick={() => setCreating(true)}
          className="px-3 h-9 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Tambah User
        </button>
      </div>

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      <div className="border border-ink-line overflow-x-auto">
        <div className="grid grid-cols-[1fr_140px_120px_120px_80px_90px_80px] min-w-[780px] gap-2 px-3 py-2 border-b border-ink bg-paper-2/60 font-mono text-[9px] uppercase tracking-widest text-ink-3">
          <span>Email · Nama</span>
          <span>Role</span>
          <span>Satker</span>
          <span>Clearance</span>
          <span>MFA</span>
          <span>Status</span>
          <span className="text-right">Aksi</span>
        </div>
        {(users || []).map((u) => (
          <div
            key={u.id}
            className="grid grid-cols-[1fr_140px_120px_120px_80px_90px_80px] min-w-[780px] gap-2 px-3 py-2.5 border-b border-dashed border-ink-line/60 last:border-b-0 font-mono text-[11px] items-center"
          >
            <div>
              <div className="font-bold text-ink">{u.full_name}</div>
              <div className="text-ink-3 text-[10px]">{u.email}</div>
            </div>
            <span className="text-bluestamp font-bold uppercase tracking-wider text-[10px]">{u.role}</span>
            <span className="text-ink-2">{u.satker || '—'}</span>
            <span className="text-maroon font-bold text-[10px] uppercase">{u.max_clearance.replace('_', ' ')}</span>
            <span className={u.mfa_enabled ? 'text-olive' : 'text-ink-3'}>{u.mfa_enabled ? '✓' : '—'}</span>
            <span className={u.is_active ? 'text-olive font-bold' : 'text-crimson'}>
              {u.is_active ? '✓ Aktif' : '✗ Nonaktif'}
            </span>
            <div className="flex justify-end gap-1">
              <button
                onClick={() => setEditing(u)}
                className="w-7 h-7 grid place-items-center border border-ink-line hover:border-brass hover:text-brass transition"
                title="Edit"
              >
                <Edit2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        {users && users.length === 0 && (
          <p className="px-4 py-8 font-mono text-xs text-ink-3 text-center italic">Tidak ada user.</p>
        )}
      </div>

      {(editing || creating) && (
        <UserModal
          user={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['admin-users'] });
          }}
        />
      )}
    </section>
  );
}

function UserModal({ user, onClose, onSaved }: { user: UserRow | null; onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState(user?.email || '');
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [role, setRole] = useState(user?.role || 'viewer');
  const [satker, setSatker] = useState(user?.satker || 'polri_pusat');
  const [region, setRegion] = useState(user?.region || '');
  const [maxClearance, setMaxClearance] = useState(user?.max_clearance || 'BIASA');
  const [nrp, setNrp] = useState(user?.nrp || '');
  const [isActive, setIsActive] = useState(user?.is_active ?? true);
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfa_enabled ?? false);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      const url = user
        ? `${import.meta.env.VITE_API_URL}/api/v1/admin/users/${user.id}`
        : `${import.meta.env.VITE_API_URL}/api/v1/admin/users`;
      const method = user ? 'PATCH' : 'POST';
      const body: any = {
        full_name: fullName,
        role,
        satker: satker || null,
        region: region || null,
        max_clearance: maxClearance,
        nrp: nrp || null,
        is_active: isActive,
        mfa_enabled: mfaEnabled,
      };
      if (!user) body.email = email;
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-Dev-User': 'admin@netra.local' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(String(e?.message || e)),
  });

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur z-[100] grid place-items-center p-8" onClick={onClose}>
      <div className="bg-paper border-2 border-ink max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-ink bg-paper-2/40">
          <UserCheck className="w-4 h-4 text-brass" />
          <h3 className="font-display font-bold text-[16px]">{user ? 'Edit User' : 'Tambah User Baru'}</h3>
          <button onClick={onClose} className="ml-auto w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); saveMut.mutate(); }}
          className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 font-mono text-[11px]"
        >
          <Field label="Email" required>
            <input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!user}
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none disabled:opacity-60"
            />
          </Field>
          <Field label="Nama Lengkap" required>
            <input
              type="text" required value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <Field label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Max Clearance">
            <select value={maxClearance} onChange={(e) => setMaxClearance(e.target.value)} className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none">
              {CLEARANCES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
            </select>
          </Field>
          <Field label="Satker">
            <input
              type="text" value={satker}
              onChange={(e) => setSatker(e.target.value)}
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <Field label="NRP/NIP">
            <input
              type="text" value={nrp}
              onChange={(e) => setNrp(e.target.value)}
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <Field label="Region">
            <input
              type="text" value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <div className="flex flex-col gap-2 pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-3.5 h-3.5 accent-brass" />
              <span className="uppercase tracking-wider">Aktif</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={mfaEnabled} onChange={(e) => setMfaEnabled(e.target.checked)} className="w-3.5 h-3.5 accent-brass" />
              <span className="uppercase tracking-wider">MFA Aktif</span>
            </label>
          </div>

          {error && (
            <div className="col-span-2 p-3 border-2 border-crimson bg-crimson/5 text-crimson text-[10px]">
              ⚠ {error}
            </div>
          )}

          <div className="col-span-2 flex gap-2 pt-4 border-t border-dashed border-ink-line">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-ink-line text-ink-2 hover:border-ink font-mono text-[11px] uppercase tracking-wider">
              Batal
            </button>
            <button
              type="submit"
              disabled={saveMut.isPending}
              className="ml-auto px-5 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              {saveMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ REGULATIONS ============

interface RegulationRow {
  id: string;
  name: string;
  full_name: string;
  category: string;
  article_text: string;
  elements: string[];
  penalty: string | null;
  version_effective: string | null;
  is_active: boolean;
}

const REG_CATEGORIES = ['PIDANA_UMUM', 'PIDANA_HARTA', 'PIDANA_KORUPSI', 'PIDANA_NARKOTIKA', 'PIDANA_ITE', 'PIDANA_KHUSUS', 'OTHER'];

function RegulationsView() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RegulationRow | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: regs, isLoading } = useQuery<RegulationRow[]>({
    queryKey: ['admin-regulations'],
    queryFn: () => api('/api/v1/admin/regulations'),
  });

  const deactivateMut = useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/regulations/${id}`,
        { method: 'DELETE', headers: { 'X-Dev-User': 'admin@netra.local' } },
      );
      if (!resp.ok) throw new Error(await resp.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-regulations'] }),
  });

  return (
    <section>
      <div className="mb-4 flex justify-end">
        <button
          onClick={() => setCreating(true)}
          className="px-3 h-9 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Tambah Pasal
        </button>
      </div>

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      <div className="space-y-2">
        {(regs || []).map((r) => (
          <div key={r.id} className={`bg-paper-2/40 border border-ink-line p-4 ${!r.is_active ? 'opacity-50' : ''}`}>
            <div className="flex items-baseline gap-3 mb-2">
              <h3 className="font-display font-bold text-[16px] tracking-tight">{r.name}</h3>
              <span className="font-mono text-[10px] uppercase tracking-wider text-bluestamp border border-bluestamp px-1.5 py-0.5">
                {r.category}
              </span>
              <span className="font-mono text-[10px] text-ink-3 ml-auto">{r.id}</span>
              {!r.is_active && <span className="font-mono text-[10px] uppercase text-crimson">NONAKTIF</span>}
              <button
                onClick={() => setEditing(r)}
                className="w-7 h-7 grid place-items-center border border-ink-line hover:border-brass hover:text-brass transition"
                title="Edit"
              >
                <Edit2 className="w-3 h-3" />
              </button>
              {r.is_active && (
                <button
                  onClick={() => {
                    if (confirm(`Nonaktifkan ${r.name}?`)) deactivateMut.mutate(r.id);
                  }}
                  className="w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition"
                  title="Nonaktifkan"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
            <p className="font-serif text-[13px] text-ink-2 leading-snug mb-2">{r.full_name}</p>
            <p className="font-serif text-[12px] text-ink leading-snug italic line-clamp-2 mb-2">{r.article_text}</p>
            <div className="flex gap-3 font-mono text-[10px] uppercase tracking-wider text-ink-3">
              <span>{r.elements.length} unsur</span>
              {r.penalty && <span>· Ancaman: {r.penalty}</span>}
              {r.version_effective && <span>· Efektif: {r.version_effective}</span>}
            </div>
          </div>
        ))}
      </div>

      {(editing || creating) && (
        <RegulationModal
          regulation={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['admin-regulations'] });
          }}
        />
      )}
    </section>
  );
}

function RegulationModal({ regulation, onClose, onSaved }: { regulation: RegulationRow | null; onClose: () => void; onSaved: () => void }) {
  const [id, setId] = useState(regulation?.id || '');
  const [name, setName] = useState(regulation?.name || '');
  const [fullName, setFullName] = useState(regulation?.full_name || '');
  const [category, setCategory] = useState(regulation?.category || REG_CATEGORIES[0]);
  const [articleText, setArticleText] = useState(regulation?.article_text || '');
  const [elementsText, setElementsText] = useState((regulation?.elements || []).join('\n'));
  const [penalty, setPenalty] = useState(regulation?.penalty || '');
  const [isActive, setIsActive] = useState(regulation?.is_active ?? true);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      const elements = elementsText.split('\n').map((s) => s.trim()).filter(Boolean);
      const body: any = {
        name, full_name: fullName, category, article_text: articleText,
        elements, penalty: penalty || null, is_active: isActive,
      };
      if (!regulation) body.id = id;
      const url = regulation
        ? `${import.meta.env.VITE_API_URL}/api/v1/admin/regulations/${regulation.id}`
        : `${import.meta.env.VITE_API_URL}/api/v1/admin/regulations`;
      const resp = await fetch(url, {
        method: regulation ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Dev-User': 'admin@netra.local' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(String(e?.message || e)),
  });

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur z-[100] grid place-items-center p-8" onClick={onClose}>
      <div className="bg-paper border-2 border-ink max-w-3xl w-full max-h-[88vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-ink bg-paper-2/40">
          <Gavel className="w-4 h-4 text-brass" />
          <h3 className="font-display font-bold text-[16px]">{regulation ? 'Edit Pasal' : 'Tambah Pasal Baru'}</h3>
          <button onClick={onClose} className="ml-auto w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate(); }} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 font-mono text-[11px]">
          <Field label="ID (slug)" required>
            <input
              type="text" required value={id}
              onChange={(e) => setId(e.target.value)}
              disabled={!!regulation}
              placeholder="uu_1_2023_pasal_372"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none disabled:opacity-60"
            />
          </Field>
          <Field label="Kategori">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none">
              {REG_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Nama Singkat" required>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Pasal 372 KUHP" className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none" />
          </Field>
          <Field label="Nama Lengkap" required>
            <input type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Pasal 372 KUHP — Penggelapan" className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none" />
          </Field>
          <Field label="Ancaman Pidana">
            <input type="text" value={penalty} onChange={(e) => setPenalty(e.target.value)} placeholder="Pidana penjara paling lama 4 tahun..." className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none" />
          </Field>
          <div className="flex items-center gap-2 pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-3.5 h-3.5 accent-brass" />
              <span className="uppercase tracking-wider">Aktif</span>
            </label>
          </div>
          <Field label="Bunyi Pasal" required cols={2}>
            <textarea required value={articleText} onChange={(e) => setArticleText(e.target.value)} rows={6} className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none font-serif text-[13px] leading-snug" />
          </Field>
          <Field label="Unsur-unsur (satu per baris)" cols={2}>
            <textarea value={elementsText} onChange={(e) => setElementsText(e.target.value)} rows={5} placeholder="Subjek hukum&#10;Memiliki barang&#10;Bukan miliknya" className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none" />
          </Field>

          {error && (
            <div className="col-span-2 p-3 border-2 border-crimson bg-crimson/5 text-crimson text-[10px]">
              ⚠ {error}
            </div>
          )}

          <div className="col-span-2 flex gap-2 pt-4 border-t border-dashed border-ink-line">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-ink-line text-ink-2 hover:border-ink font-mono text-[11px] uppercase tracking-wider">
              Batal
            </button>
            <button type="submit" disabled={saveMut.isPending} className="ml-auto px-5 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-2 disabled:opacity-50">
              {saveMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ SATKERS (read-only) ============

interface SatkerRow {
  id: string;
  name: string;
  region: string | null;
  parent_id: string | null;
  is_active: boolean;
}

function SatkersView() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SatkerRow | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: satkers, isLoading } = useQuery<SatkerRow[]>({
    queryKey: ['admin-satkers'],
    queryFn: () => api('/api/v1/admin/satkers'),
  });

  const deactivateMut = useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/satkers/${id}`,
        { method: 'DELETE', headers: { 'X-Dev-User': 'admin@netra.local' } },
      );
      if (!resp.ok) throw new Error(await resp.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-satkers'] }),
  });

  return (
    <section>
      <div className="mb-4 flex justify-end">
        <button
          onClick={() => setCreating(true)}
          className="px-3 h-9 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Tambah Satker
        </button>
      </div>

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      <div className="border border-ink-line overflow-x-auto">
        <div className="grid grid-cols-[1fr_2fr_140px_120px_80px_100px] min-w-[720px] gap-2 px-3 py-2 border-b border-ink bg-paper-2/60 font-mono text-[9px] uppercase tracking-widest text-ink-3">
          <span>ID</span>
          <span>Nama</span>
          <span>Region</span>
          <span>Parent</span>
          <span>Aktif</span>
          <span className="text-right">Aksi</span>
        </div>
        {(satkers || []).map((s) => (
          <div
            key={s.id}
            className={`grid grid-cols-[1fr_2fr_140px_120px_80px_100px] min-w-[720px] gap-2 px-3 py-2 border-b border-dashed border-ink-line/60 last:border-b-0 font-mono text-[11px] items-center ${
              !s.is_active ? 'opacity-50' : ''
            }`}
          >
            <span className="text-bluestamp font-bold truncate">{s.id}</span>
            <span className="text-ink font-bold">{s.name}</span>
            <span className="text-ink-2">{s.region || '—'}</span>
            <span className="text-ink-3 truncate">{s.parent_id || '—'}</span>
            <span className={s.is_active ? 'text-olive font-bold' : 'text-crimson'}>
              {s.is_active ? '✓' : '✗'}
            </span>
            <div className="flex justify-end gap-1">
              <button
                onClick={() => setEditing(s)}
                className="w-7 h-7 grid place-items-center border border-ink-line hover:border-brass hover:text-brass transition"
                title="Edit"
              >
                <Edit2 className="w-3 h-3" />
              </button>
              {s.is_active && (
                <button
                  onClick={() => {
                    if (confirm(`Nonaktifkan ${s.name}?`)) deactivateMut.mutate(s.id);
                  }}
                  className="w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition"
                  title="Nonaktifkan"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {satkers && satkers.length === 0 && (
          <p className="px-4 py-8 font-mono text-xs text-ink-3 text-center italic">Belum ada satker.</p>
        )}
      </div>

      {(editing || creating) && (
        <SatkerModal
          satker={editing}
          allSatkers={satkers || []}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['admin-satkers'] });
          }}
        />
      )}
    </section>
  );
}

function SatkerModal({ satker, allSatkers, onClose, onSaved }: { satker: SatkerRow | null; allSatkers: SatkerRow[]; onClose: () => void; onSaved: () => void }) {
  const [id, setId] = useState(satker?.id || '');
  const [name, setName] = useState(satker?.name || '');
  const [region, setRegion] = useState(satker?.region || '');
  const [parentId, setParentId] = useState(satker?.parent_id || '');
  const [active, setActive] = useState(satker?.is_active ?? true);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      const body: any = {
        name,
        region: region || null,
        parent_id: parentId || null,
        active,
      };
      if (!satker) body.id = id;
      const url = satker
        ? `${import.meta.env.VITE_API_URL}/api/v1/admin/satkers/${satker.id}`
        : `${import.meta.env.VITE_API_URL}/api/v1/admin/satkers`;
      const resp = await fetch(url, {
        method: satker ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Dev-User': 'admin@netra.local' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(String(e?.message || e)),
  });

  // Filter parent options to exclude self (saat edit)
  const parentOptions = allSatkers.filter((s) => !satker || s.id !== satker.id);

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur z-[100] grid place-items-center p-8" onClick={onClose}>
      <div className="bg-paper border-2 border-ink max-w-xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-ink bg-paper-2/40">
          <Building2 className="w-4 h-4 text-brass" />
          <h3 className="font-display font-bold text-[16px]">{satker ? 'Edit Satker' : 'Tambah Satker Baru'}</h3>
          <button onClick={onClose} className="ml-auto w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate(); }} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 font-mono text-[11px]">
          <Field label="ID (slug)" required>
            <input
              type="text" required value={id}
              onChange={(e) => setId(e.target.value)}
              disabled={!!satker}
              placeholder="polda_metro"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none disabled:opacity-60"
            />
          </Field>
          <Field label="Nama" required>
            <input
              type="text" required value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Polda Metro Jaya"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <Field label="Region">
            <input
              type="text" value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="dki_jakarta"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <Field label="Parent Satker">
            <select value={parentId} onChange={(e) => setParentId(e.target.value)} className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none">
              <option value="">— Tanpa parent —</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.id} · {p.name}</option>
              ))}
            </select>
          </Field>
          <div className="flex items-center gap-2 pt-3 col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-3.5 h-3.5 accent-brass" />
              <span className="uppercase tracking-wider">Aktif</span>
            </label>
          </div>

          {error && (
            <div className="col-span-2 p-3 border-2 border-crimson bg-crimson/5 text-crimson text-[10px]">
              ⚠ {error}
            </div>
          )}

          <div className="col-span-2 flex gap-2 pt-4 border-t border-dashed border-ink-line">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-ink-line text-ink-2 hover:border-ink font-mono text-[11px] uppercase tracking-wider">
              Batal
            </button>
            <button type="submit" disabled={saveMut.isPending} className="ml-auto px-5 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-2 disabled:opacity-50">
              {saveMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface RoleRow {
  name: string;
  label: string;
  description: string | null;
  allowed_tabs: string[];
  is_system: boolean;
  created_at: string | null;
  updated_at: string | null;
}

const TAB_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  berkas: 'Berkas',
  analisa: 'Analisa',
  audit: 'Audit',
  admin: 'Administrasi',
};

function RolesView() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: roles, isLoading } = useQuery<RoleRow[]>({
    queryKey: ['admin-roles'],
    queryFn: () => api('/api/v1/roles'),
  });
  const { data: tabsData } = useQuery<{ tabs: string[] }>({
    queryKey: ['admin-role-tabs'],
    queryFn: () => api('/api/v1/roles/tabs'),
  });
  const tabs = tabsData?.tabs || [];

  const deleteMut = useMutation({
    mutationFn: (name: string) => api(`/api/v1/roles/${name}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-roles'] }),
    onError: (e: any) => alert(String(e?.message || e)),
  });

  const cols = `220px repeat(${tabs.length || 1}, minmax(80px,1fr)) 90px`;
  const minW = 220 + (tabs.length || 1) * 90 + 90;

  return (
    <section>
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <p className="font-mono text-[11px] text-ink-2">
          Matrix <strong className="text-ink">role × menu</strong> — centang menu yang boleh diakses tiap role.
          Akses per-BAP tetap diatur OPA. Role bawaan sistem tidak bisa dihapus; super_admin selalu penuh.
        </p>
        <button
          onClick={() => setCreating(true)}
          className="ml-auto px-3 h-9 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Tambah Role
        </button>
      </div>

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      <div className="border border-ink-line overflow-x-auto">
        <div
          className="grid gap-2 px-3 py-2 border-b border-ink bg-paper-2/60 font-mono text-[9px] uppercase tracking-widest text-ink-3"
          style={{ gridTemplateColumns: cols, minWidth: minW }}
        >
          <span>Role</span>
          {tabs.map((t) => (
            <span key={t} className="text-center">{TAB_LABEL[t] || t}</span>
          ))}
          <span className="text-right">Aksi</span>
        </div>
        {(roles || []).map((r) => (
          <div
            key={r.name}
            className="grid gap-2 px-3 py-2.5 border-b border-dashed border-ink-line/60 last:border-b-0 font-mono text-[11px] items-center"
            style={{ gridTemplateColumns: cols, minWidth: minW }}
          >
            <div className="min-w-0">
              <div className="font-bold text-ink truncate" title={r.description || ''}>{r.label}</div>
              <div className="text-ink-3 text-[9px] truncate">
                {r.name}{r.is_system && ' · sistem'}
              </div>
            </div>
            {tabs.map((t) => {
              const on = r.allowed_tabs.includes(t);
              return (
                <span key={t} className="text-center">
                  {on
                    ? <Check className="w-3.5 h-3.5 text-olive inline" />
                    : <span className="text-ink-line">·</span>}
                </span>
              );
            })}
            <div className="flex justify-end gap-1">
              <button
                onClick={() => setEditing(r)}
                className="w-7 h-7 grid place-items-center border border-ink-line hover:border-brass hover:text-brass transition"
                title="Edit"
              >
                <Edit2 className="w-3 h-3" />
              </button>
              <button
                onClick={() => {
                  if (r.is_system) { alert('Role bawaan sistem tidak bisa dihapus.'); return; }
                  if (confirm(`Hapus role ${r.label}?`)) deleteMut.mutate(r.name);
                }}
                disabled={r.is_system}
                className="w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition disabled:opacity-30 disabled:cursor-not-allowed"
                title={r.is_system ? 'Role sistem — tidak bisa dihapus' : 'Hapus'}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        {roles && roles.length === 0 && (
          <p className="px-4 py-8 font-mono text-xs text-ink-3 text-center italic">Belum ada role.</p>
        )}
      </div>

      {(editing || creating) && (
        <RoleModal
          role={editing}
          tabs={tabs}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['admin-roles'] });
          }}
        />
      )}
    </section>
  );
}

function RoleModal({ role, tabs, onClose, onSaved }: { role: RoleRow | null; tabs: string[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(role?.name || '');
  const [label, setLabel] = useState(role?.label || '');
  const [description, setDescription] = useState(role?.description || '');
  const [allowedTabs, setAllowedTabs] = useState<string[]>(role?.allowed_tabs || ['dashboard', 'berkas', 'analisa']);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = role?.name === 'super_admin';

  const toggleTab = (t: string) => {
    if (isSuperAdmin) return;
    setAllowedTabs((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      if (role) {
        return api(`/api/v1/roles/${role.name}`, {
          method: 'PATCH',
          body: JSON.stringify({ label, description: description || null, allowed_tabs: allowedTabs }),
        });
      }
      return api('/api/v1/roles', {
        method: 'POST',
        body: JSON.stringify({ name, label, description: description || null, allowed_tabs: allowedTabs }),
      });
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(String(e?.message || e)),
  });

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur z-[100] grid place-items-center p-8" onClick={onClose}>
      <div className="bg-paper border-2 border-ink max-w-xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-ink bg-paper-2/40">
          <ShieldCheck className="w-4 h-4 text-brass" />
          <h3 className="font-display font-bold text-[16px]">{role ? `Edit Role · ${role.label}` : 'Tambah Role Baru'}</h3>
          <button onClick={onClose} className="ml-auto w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate(); }} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 font-mono text-[11px]">
          <Field label="Nama (slug)" required>
            <input
              type="text" required value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!!role}
              placeholder="reviewer_eksternal"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none disabled:opacity-60"
            />
          </Field>
          <Field label="Label" required>
            <input
              type="text" required value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Reviewer Eksternal"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <Field label="Deskripsi" cols={2}>
            <input
              type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ringkasan tanggung jawab role"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <div className="col-span-2">
            <label className="block label-mono mb-1.5">
              Menu yang boleh diakses {isSuperAdmin && <span className="text-ink-3">(super_admin selalu penuh)</span>}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {tabs.map((t) => {
                const on = isSuperAdmin || allowedTabs.includes(t);
                return (
                  <label
                    key={t}
                    className={`flex items-center gap-2 px-2.5 py-2 border cursor-pointer transition ${
                      on ? 'border-brass bg-brass-soft/40 text-ink' : 'border-ink-line text-ink-2'
                    } ${isSuperAdmin ? 'cursor-not-allowed opacity-70' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={isSuperAdmin}
                      onChange={() => toggleTab(t)}
                      className="w-3.5 h-3.5 accent-brass"
                    />
                    <span className="uppercase tracking-wider text-[10px]">{TAB_LABEL[t] || t}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="col-span-2 p-3 border-2 border-crimson bg-crimson/5 text-crimson text-[10px]">
              ⚠ {error}
            </div>
          )}

          <div className="col-span-2 flex gap-2 pt-4 border-t border-dashed border-ink-line">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-ink-line text-ink-2 hover:border-ink font-mono text-[11px] uppercase tracking-wider">
              Batal
            </button>
            <button type="submit" disabled={saveMut.isPending} className="ml-auto px-5 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-2 disabled:opacity-50">
              {saveMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, required, cols }: { label: string; children: React.ReactNode; required?: boolean; cols?: number }) {
  return (
    <div className={cols === 2 ? 'col-span-2' : ''}>
      <label className="block label-mono mb-1">
        {label} {required && <span className="text-crimson">*</span>}
      </label>
      {children}
    </div>
  );
}
